import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  useAddRFIDTagMutation,
  useUpdateRFIDTagMutation,
} from "../../../../../redux/api/ev-charger/evChargerAPI";
import AddOcppTagRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/add-ccpp-tag-request-dto";
import UpdateOcppTagRequestDTO from "../../../../../shared/oversight-core/dtos/request-dtos/update-ocpp-tag-request-dto";
import OcppTagResponseDTO from "../../../../../shared/oversight-core/dtos/response-dtos/ocpp-tag-response-dto";
import { IHttpError } from "../../../../../shared/oversight-core/interfaces/http-errror";
import AppButton from "../../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import AppInput from "../../../../../shared/oversight-core/ui-elements/input/app-input";
import MaterialIcon from "../../../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../../shared/oversight-core/utils/toast";
import AssignUserModal from "../assign-user-modal/assign-user-modal";
import ScanRfidModal from "../scan-rfid-modal/scan-rfid-modal";

interface IFormInput {
  idTag: string;
  note: string;
}

const defaultFormValues: IFormInput = {
  idTag: "",
  note: "",
};

interface IProps extends ModelContainerProps {
  show: boolean;
  tagData?: OcppTagResponseDTO | null;
  onSuccess?: () => void;
}

const AddUpdateRfidModal = (props: IProps) => {
  const { show, tagData, onClose, onCancel, onSuccess, ...rest } = props;

  const isEdit = !!tagData;

  const [linkedUser, setLinkedUser] = useState<{
    consumerId: string;
    name: string;
  } | null>(null);
  const [showAssignUserModal, setShowAssignUserModal] = useState(false);
  const [showScanRfidModal, setShowScanRfidModal] = useState(false);

  const [addRFIDTag, { isLoading: isAdding }] = useAddRFIDTagMutation();
  const [updateRFIDTag, { isLoading: isUpdating }] = useUpdateRFIDTagMutation();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const watchIdTag = watch("idTag") || "";
  const watchNote = watch("note") || "";

  const isConfirmDisabled = isEdit
    ? !watchNote.trim() && !linkedUser
    : !watchIdTag.trim();

  useEffect(() => {
    if (!show) {
      reset({ ...defaultFormValues });
      setLinkedUser(null);
      setShowAssignUserModal(false);
      setShowScanRfidModal(false);
    } else {
      if (tagData) {
        reset({
          idTag: tagData.idTag || "",
          note: tagData.note || "",
        });
        if (tagData.consumerId || tagData.holderName) {
          setLinkedUser({
            consumerId: tagData.consumerId || "",
            name: tagData.holderName || "",
          });
        } else {
          setLinkedUser(null);
        }
      } else {
        reset({ ...defaultFormValues });
        setLinkedUser(null);
      }
    }
  }, [show, tagData, reset]);

  const handleResetAndClose = () => {
    reset({ ...defaultFormValues });
    setLinkedUser(null);
    setShowAssignUserModal(false);
    setShowScanRfidModal(false);
    if (onClose) onClose();
    if (onCancel) onCancel();
  };

  const onSubmit = (data: IFormInput) => {
    const consumerId = linkedUser?.consumerId || null;

    if (isEdit && tagData) {
      const payload: UpdateOcppTagRequestDTO = {
        ocppTagId: tagData.ocppTagId,
        note: data.note || null,
        blocked: tagData.blocked ?? false,
        consumerId: consumerId,
      };

      updateRFIDTag(payload)
        .unwrap()
        .then(() => {
          showSuccessMessage("RFID Tag updated successfully");
          if (onSuccess) onSuccess();
          handleResetAndClose();
        })
        .catch((err: IHttpError) => {
          showErrorMessage(err.message || "Failed to update RFID Tag");
        });
    } else {
      const payload: AddOcppTagRequestDTO = {
        idTag: data.idTag,
        note: data.note || null,
        consumerId: consumerId || null,
        unowned: !consumerId,
      };

      addRFIDTag(payload)
        .unwrap()
        .then(() => {
          showSuccessMessage("RFID Tag added successfully");
          if (onSuccess) onSuccess();
          handleResetAndClose();
        })
        .catch((err: IHttpError) => {
          showErrorMessage(err.message || "Failed to add RFID Tag");
        });
    }
  };

  return (
    <>
      <ModalContainer
        show={show}
        {...rest}
        onClose={handleResetAndClose}
        onCancel={handleResetAndClose}
        onConfirm={handleSubmit(onSubmit)}
        title={isEdit ? "Update RFID" : "Add RFID"}
        confirmButtonText="Confirm"
        cancelButtonText="Cancel"
        size="modal-md"
        isLoading={isAdding || isUpdating}
        disabled={isConfirmDisabled}
      >
        <>
          <Row
            className={errors.idTag ? `align-items-center` : `align-items-end`}
          >
            <Col>
              <AppInput
                label="RFID"
                placeholder="Enter RFID"
                name="idTag"
                register={register("idTag", {
                  required: "RFID is required",
                })}
                errors={errors}
                disabled={isEdit}
              />
            </Col>
            {!isEdit && (
              <Col xs="auto" className="ps-0">
                <AppButton
                  text="Scan"
                  type="button"
                  variant="secondary"
                  iconName="sensors"
                  onClick={() => setShowScanRfidModal(true)}
                  style={{ height: "44px" }}
                />
              </Col>
            )}
          </Row>
          <AppInput
            className="mt-3"
            label="Note"
            placeholder="Enter Note"
            name="note"
            register={register("note")}
            errors={errors}
            isRequired={false}
          />
          <div className="mt-3">
            <label className="font-size-14 font-weight-500 text-dark mb-2 d-block">
              Assign User
            </label>
            {linkedUser ? (
              <div className="container-dash d-flex align-items-center justify-content-between p-3">
                <div className="d-flex align-items-center gap-2">
                  <MaterialIcon icon="person" color="#7483CC" size={20} />
                  <span className="font-size-14 font-weight-500 text-dark">
                    {linkedUser.name || linkedUser.consumerId}
                  </span>
                </div>
                <div
                  className="d-flex align-items-center gap-1 cursor-pointer text-danger font-size-14 font-weight-500"
                  onClick={() => setLinkedUser(null)}
                >
                  <MaterialIcon icon="link_off" color="#dc3545" size={18} />
                  <span>Unlink</span>
                </div>
              </div>
            ) : (
              <div
                className="container-dash d-flex align-items-center justify-content-center gap-2 p-3 cursor-pointer"
                onClick={() => setShowAssignUserModal(true)}
              >
                <MaterialIcon icon="link" color="#7483CC" size={20} />
                <span className="font-size-14 font-weight-500 text-primary">
                  Link User
                </span>
              </div>
            )}
          </div>
        </>
      </ModalContainer>
      <AssignUserModal
        show={showAssignUserModal}
        tagData={tagData}
        onClose={() => setShowAssignUserModal(false)}
        onCancel={() => setShowAssignUserModal(false)}
        onSelectUser={(user) => {
          setLinkedUser({
            consumerId: user.identity,
            name: user.name,
          });
          setShowAssignUserModal(false);
        }}
      />
      <ScanRfidModal
        show={showScanRfidModal}
        onClose={() => setShowScanRfidModal(false)}
        onCancel={() => setShowScanRfidModal(false)}
        onScanSuccess={(scannedTagId) => {
          setValue("idTag", scannedTagId);
          setShowScanRfidModal(false);
        }}
      />
    </>
  );
};

export default AddUpdateRfidModal;
